import { useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { toast } from 'sonner';
import {
  Archive, Ban, ChevronRight, Code2, Copy, Search, Download, ExternalLink, Flag, FolderClosed, Forward, FolderInput, Mail, MoreHorizontal, Paperclip, Printer,
  Reply, ReplyAll, ShieldAlert, ShieldCheck, Sparkles, Trash2, UserPlus, Workflow, Send,
} from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { displayName, domainOf, formatBytes, parseAddressList, parseMailbox, htmlToPlain, type MessageDetail, type MessageSummary } from '@dts/core';
import { api, errorMessage } from '@/lib/api';
import { announceBlocked, qk, useFolders, useMailActions, useMessage, useMyAddresses, useThread, usePrefs, useContacts } from '@/lib/queries';
import { composeForward, composeNew, composeReply } from '@/lib/compose';
import { runCrmTask } from '@/lib/crm';
import { fullTime, listTime } from '@/lib/format';
import { useUi } from '@/lib/store';
import { Avatar, Favicon } from './Avatar';
import { MessageBody, printMessage } from './MessageBody';
import { InsightsCard } from './InsightsCard';
import { Button, cn, Empty, IconButton, Modal, Num, Spinner } from './ui';

export function Reader({ id }: { id: string }) {
  const thread = useThread(id);
  const root = useMessage(id);
  const actions = useMailActions();
  const qc = useQueryClient();
  const select = useUi((s) => s.select);
  const view = useUi((s) => s.view);
  const setView = useUi((s) => s.setView);
  const folders = useFolders().data ?? [];
  const mine = useMyAddresses();
  const prefs = usePrefs().data;
  const contacts = useContacts().data?.contacts ?? [];
  const [moreOpen, setMoreOpen] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);
  const [source, setSource] = useState<string | null>(null);
  const [crmLookup, setCrmLookup] = useState<{ loading: boolean; text?: string } | null>(null);
  const scroller = useRef<HTMLDivElement>(null);
  // Email: just the message that was clicked. Conversation: the whole thread.
  const [tab, setTab] = useState<'email' | 'conversation'>('email');

  const messages: MessageSummary[] = useMemo(() => {
    if (thread.data?.length) return thread.data;
    return root.data ? [root.data.summary] : [];
  }, [thread.data, root.data]);
  const latest = messages[messages.length - 1];
  const latestDetail = useMessage(latest?.id ?? null).data;
  const subject = root.data?.summary.subject ?? latest?.subject ?? '';

  // Mark the conversation read shortly after it is opened.
  useEffect(() => {
    const unread = messages.filter((m) => !m.isRead).map((m) => m.id);
    if (unread.length === 0) return;
    const t = setTimeout(() => actions.run(unread, 'read'), 700);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages.map((m) => m.id + m.isRead).join()]);

  useEffect(() => {
    scroller.current?.scrollTo({ top: 0 });
    setCrmLookup(null);
  }, [id]);

  const ids = messages.map((m) => m.id);
  // Actions (reply, flag, print…) apply to what's on screen: the clicked message, or the newest one in the thread.
  const target = tab === 'email' ? root.data ?? latestDetail : latestDetail ?? root.data;
  const sender = target ? parseMailbox(target.summary.from) ?? { address: target.summary.fromAddress } : null;
  const isJunk = target?.summary.folder === 'Junk';
  const senderKnown = sender && contacts.some((c) => c.emails.includes(sender.address));
  const advance = () => select(null);

  // Single-key shortcuts for the open conversation.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement;
      if (t.isContentEditable || /INPUT|TEXTAREA|SELECT/.test(t.tagName) || e.metaKey || e.ctrlKey || e.altKey || !target) return;
      const k = e.key;
      if (k === 'r') composeReply(target, false);
      else if (k === 'a') composeReply(target, true);
      else if (k === 'f') composeForward(target);
      else if (k === 'e') (actions.run(ids, 'archive'), advance());
      else if (k === '#' || k === 'Delete') (actions.run(ids, view.kind === 'mail' && view.folder === 'Trash' ? 'delete' : 'trash'), advance());
      else if (k === 's') actions.run([target.summary.id], target.summary.isFlagged ? 'unflag' : 'flag');
      else if (k === 'u') (actions.run(ids, 'unread'), advance());
      else if (k === '!') (actions.run(ids, 'junk'), advance());
      else return;
      e.preventDefault();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const addContact = async () => {
    if (!target || !sender) return;
    const t = toast.loading('Building contact card with AI…');
    try {
      const x = await api.extractContact(target.summary.id).catch(() => null);
      await api.createContact({
        name: x?.name || displayName(sender),
        emails: Array.from(new Set([sender.address, ...(x?.emails ?? [])].map((e) => e.toLowerCase()))),
        phones: x?.phones ?? [],
        company: x?.company || undefined,
        title: x?.title || undefined,
        website: x?.website || undefined,
        address: x?.address || undefined,
        tags: [],
        favorite: false,
        source: 'email',
      });
      qc.invalidateQueries({ queryKey: qk.contacts });
      toast.success(`Added ${x?.name || displayName(sender)} to contacts`, { id: t });
    } catch (e) {
      toast.error(errorMessage(e), { id: t });
    }
  };

  const lookupCrm = async () => {
    if (!sender) return;
    setCrmLookup({ loading: true });
    const me = qc.getQueryData<{ user: { displayName: string } }>(qk.me);
    const r = await runCrmTask({ kind: 'lookup', person: displayName(sender), email: sender.address, userName: me?.user.displayName ?? 'the user' }, `CRM lookup: ${sender.address}`);
    setCrmLookup({ loading: false, text: r?.summary ?? 'CRM lookup failed — check Settings → CRM.' });
  };

  const logCrm = async () => {
    if (!target) return;
    const me = qc.getQueryData<{ user: { displayName: string } }>(qk.me);
    const outbound = mine.includes(target.summary.fromAddress.toLowerCase());
    await runCrmTask({
      kind: 'log',
      userName: me?.user.displayName ?? 'the user',
      instructions: prefs?.crm?.instructions,
      email: {
        direction: outbound ? 'sent' : 'received',
        from: target.summary.from,
        to: parseAddressList(target.summary.to).map((m) => m.address),
        cc: parseAddressList(target.summary.cc).map((m) => m.address),
        subject: target.summary.subject,
        date: target.summary.sentDate ?? target.summary.receivedUtc,
        body: target.textBody ?? htmlToPlain(target.htmlBody ?? ''),
      },
    }, `Logging “${target.summary.subject}” to CRM`, target.summary.id);
  };

  if (root.isError) return <Empty icon={<Mail />} title="Message not found" hint={errorMessage(root.error)} />;
  if (!root.data && root.isLoading) return <div className="flex h-full items-center justify-center"><Spinner className="size-6" /></div>;

  return (
    <div className="flex h-full min-w-0 flex-col">
      {/* toolbar */}
      <div className="flex h-12 shrink-0 items-center gap-0.5 px-4">
        {target && (
          <>
            <IconButton label="Reply (R)" onClick={() => composeReply(target, false)}><Reply /></IconButton>
            <IconButton label="Reply all (A)" onClick={() => composeReply(target, true)}><ReplyAll /></IconButton>
            <IconButton label="Forward (F)" onClick={() => composeForward(target)}><Forward /></IconButton>
            <div className="mx-1.5 h-5 w-px bg-line" />
            <IconButton label="Archive (E)" onClick={() => (actions.run(ids, 'archive'), advance())}><Archive /></IconButton>
            <IconButton label="Delete (#)" onClick={() => (actions.run(ids, view.kind === 'mail' && view.folder === 'Trash' ? 'delete' : 'trash'), advance())}><Trash2 /></IconButton>
            {isJunk ? (
              <IconButton label="Not junk" onClick={() => (api.markNotJunk(target.summary.id, true).then(() => actions.refresh()), advance(), toast.success('Moved to Inbox and sender safe-listed'))}><ShieldCheck /></IconButton>
            ) : (
              <IconButton label="Junk (!)" onClick={() => (actions.run(ids, 'junk'), advance())}><ShieldAlert /></IconButton>
            )}
            <div className="relative">
              <IconButton label="Move to…" onClick={() => setMoveOpen(!moveOpen)}><FolderInput /></IconButton>
              <Popover open={moveOpen} onClose={() => setMoveOpen(false)}>
                {folders.filter((f) => !['Drafts', 'Sent', target.summary.folder].includes(f.name)).map((f) => (
                  <MenuItem key={f.name} onClick={() => (actions.run(ids, 'move', f.name), setMoveOpen(false), advance(), toast.success(`Moved to ${f.name}`))}>{f.name}</MenuItem>
                ))}
              </Popover>
            </div>
            <IconButton label="Flag (S)" active={target.summary.isFlagged} onClick={() => actions.run([target.summary.id], target.summary.isFlagged ? 'unflag' : 'flag')}><Flag /></IconButton>
            <IconButton label="Mark unread (U)" onClick={() => (actions.run(ids, 'unread'), advance())}><Mail /></IconButton>
            <div className="flex-1" />
            <Button size="sm" variant="ghost" icon={<Workflow className="size-4" />} onClick={logCrm} title="Log this email to your CRM">Log to CRM</Button>
            <div className="relative">
              <IconButton label="More" onClick={() => setMoreOpen(!moreOpen)}><MoreHorizontal /></IconButton>
              <Popover open={moreOpen} onClose={() => setMoreOpen(false)} align="right">
                {!senderKnown && <MenuItem icon={<UserPlus />} onClick={() => (setMoreOpen(false), void addContact())}>Add sender to contacts (AI)</MenuItem>}
                <MenuItem icon={<Workflow />} onClick={() => (setMoreOpen(false), void lookupCrm())}>Look up sender in CRM</MenuItem>
                <MenuItem icon={<Printer />} onClick={() => (setMoreOpen(false), printMessage(target))}>Print</MenuItem>
                <MenuItem icon={<Code2 />} onClick={() => (setMoreOpen(false), api.rawMessage(target.summary.id).then(setSource).catch((e) => toast.error(errorMessage(e))))}>View source</MenuItem>
                <MenuItem icon={<Ban />} onClick={() => (setMoreOpen(false), api.markJunk(target.summary.id, true).then((r) => (actions.refresh(), announceBlocked([target.summary.fromAddress], r.alsoJunked, actions.refresh), r.unsubscribed?.length && toast.success(`Unsubscribed from ${r.unsubscribed[0]}`))), advance())}>Block sender</MenuItem>
                <MenuItem icon={<ShieldCheck />} onClick={() => (setMoreOpen(false), api.addToSenderList('safe', [target.summary.fromAddress]).then(() => toast.success('Sender added to safe list')))}>Always trust sender</MenuItem>
              </Popover>
            </div>
          </>
        )}
      </div>

      <div ref={scroller} className="min-h-0 flex-1 overflow-y-auto px-6 pb-10">
        <div className="mx-auto max-w-[860px]">
          <motion.h2 key={id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="selectable pb-1 pt-2 text-[24px] font-semibold leading-tight tracking-tight">
            {subject || '(no subject)'}
          </motion.h2>
          <div className="mb-4 flex flex-wrap items-center gap-2 text-[12px] text-muted">
            {messages.length > 1 && <span>{messages.length} messages</span>}
            {root.data && (view.kind !== 'mail' || view.search || view.folder !== root.data.summary.folder) && (
              <button
                title={`Open ${root.data.summary.folder}`}
                onClick={() => setView({ kind: 'mail', folder: root.data!.summary.folder })}
                className="inline-flex items-center gap-1 rounded-full bg-line px-2 py-0.5 transition hover:text-fg"
              >
                <FolderClosed className="size-3" />
                {root.data.summary.folder.split('/').map((part, i) => (
                  <span key={i} className="inline-flex items-center gap-1">
                    {i > 0 && <ChevronRight className="size-3 text-faint" />}
                    {part}
                  </span>
                ))}
              </button>
            )}
            {root.data?.summary.recipientAddress && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-line px-2 py-0.5">
                <Favicon domain={domainOf(root.data.summary.recipientAddress)} size={12} />
                to {root.data.summary.recipientAddress}
              </span>
            )}
            {target?.summary.aiCategory === 'needs_reply' && <span className="rounded-full bg-danger/15 px-2 py-0.5 font-semibold text-danger">Needs reply</span>}
          </div>

          {isJunk && target?.junkReason && (
            <div className="mb-4 flex items-center gap-3 rounded-xl border border-warn/30 bg-warn/10 px-3 py-2 text-[12.5px]">
              <ShieldAlert className="size-4 text-warn" />
              <span className="flex-1">Filed as junk: {target.junkReason}</span>
              <Button size="sm" variant="soft" onClick={() => (api.markNotJunk(target.summary.id, true).then(() => actions.refresh()), advance())}>Not junk</Button>
            </div>
          )}

          <AnimatePresence>
            {crmLookup && (
              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="mb-4 overflow-hidden">
                <div className={cn('ai-ring rounded-2xl bg-raised/70 px-4 py-3', crmLookup.loading && 'busy')}>
                  <div className="mb-1 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-faint"><Workflow className="size-3.5" />CRM</div>
                  <div className="selectable text-[13.5px]">{crmLookup.loading ? <span className="shimmer-text">Searching your CRM…</span> : crmLookup.text}</div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="mb-4 flex gap-1 border-b border-line">
            {([['email', 'Email'], ['conversation', messages.length > 1 ? `Conversation (${messages.length})` : 'Conversation']] as const).map(([key, label]) => (
              <button
                key={key}
                onClick={() => setTab(key)}
                className={cn(
                  '-mb-px border-b-2 px-3 pb-2 pt-1 text-[13px] font-medium transition',
                  tab === key ? 'border-accent text-fg' : 'border-transparent text-muted hover:text-fg',
                )}
              >
                {label}
              </button>
            ))}
          </div>

          {target && <QuickReply detail={target} />}
        </div>
          {tab === 'email' ? (
            root.data && <ThreadMessage key={root.data.summary.id} summary={root.data.summary} defaultOpen fixed mine={mine} />
          ) : (
            <>
              <div className="mb-5">
                <InsightsCard messageId={latest?.id ?? id} threadSize={messages.length} latest={latestDetail} />
              </div>

              <div className="space-y-3">
                {messages.map((m, i) => (
                  <ThreadMessage key={m.id} summary={m} defaultOpen={i === messages.length - 1 || !m.isRead || m.id === id} mine={mine} />
                ))}
              </div>
            </>
          )}

      </div>

      <Modal open={source !== null} onClose={() => setSource(null)} width={900} title="Message source">
        <pre className="selectable whitespace-pre-wrap break-all p-5 font-mono text-[11.5px] text-muted">{source}</pre>
      </Modal>
    </div>
  );
}

/** The sender's address in a message header; clicking it opens what you'd want to do with that person. */
function SenderMenu({ id, from }: { id: string; from: { name?: string; address: string } }) {
  const [open, setOpen] = useState(false);
  const qc = useQueryClient();
  const setView = useUi((s) => s.setView);
  const contacts = useContacts().data?.contacts ?? [];
  const known = contacts.some((c) => c.emails.includes(from.address.toLowerCase()));
  const name = displayName(from);
  const close = () => setOpen(false);
  const copy = (text: string, what: string) => (close(), navigator.clipboard.writeText(text).then(() => toast.success(`Copied ${what}`)));
  const add = async () => {
    close();
    const t = toast.loading('Building contact card with AI…');
    try {
      const x = await api.extractContact(id).catch(() => null);
      await api.createContact({
        name: x?.name || name,
        emails: Array.from(new Set([from.address, ...(x?.emails ?? [])].map((e) => e.toLowerCase()))),
        phones: x?.phones ?? [], company: x?.company || undefined, title: x?.title || undefined,
        website: x?.website || undefined, address: x?.address || undefined, tags: [], favorite: false, source: 'email',
      });
      qc.invalidateQueries({ queryKey: qk.contacts });
      toast.success(`Added ${x?.name || name} to contacts`, { id: t });
    } catch (e) {
      toast.error(errorMessage(e), { id: t });
    }
  };
  return (
    <div className="relative min-w-0" onClick={(e) => e.stopPropagation()}>
      <button onClick={() => setOpen(!open)} title="Sender actions" className="max-w-full truncate rounded px-1 text-[12px] text-faint transition hover:bg-line hover:text-fg">{from.address}</button>
      <Popover open={open} onClose={close}>
        <div className="truncate px-2.5 py-1.5 text-[11.5px] text-faint">{from.address}</div>
        <MenuItem icon={<Mail />} onClick={() => (close(), composeNew({ to: [from.address] }))}>Send email</MenuItem>
        <MenuItem icon={<Copy />} onClick={() => copy(from.address, 'email address')}>Copy address</MenuItem>
        {name !== from.address && <MenuItem icon={<Copy />} onClick={() => copy(`${name} <${from.address}>`, 'name and address')}>Copy name and address</MenuItem>}
        <MenuItem icon={<Search />} onClick={() => (close(), setView({ kind: 'mail', search: `from:${from.address}` }))}>Search mail from sender</MenuItem>
        {!known && <MenuItem icon={<UserPlus />} onClick={() => void add()}>Add to contacts</MenuItem>}
        <MenuItem icon={<ShieldCheck />} onClick={() => (close(), api.addToSenderList('safe', [from.address]).then(() => toast.success('Sender added to safe list')))}>Always trust sender</MenuItem>
        <MenuItem icon={<Ban />} onClick={() => (close(), api.addToSenderList('block', [from.address]).then(() => toast.success('Sender blocked')).catch((e) => toast.error(errorMessage(e))))}>Block sender</MenuItem>
      </Popover>
    </div>
  );
}

/** One message of a thread. fixed: always expanded, with no collapse toggle (the Email tab). */
function ThreadMessage({ summary, defaultOpen, mine, fixed }: { summary: MessageSummary; defaultOpen: boolean; mine: string[]; fixed?: boolean }) {
  const [open, setOpen] = useState(defaultOpen || !!fixed);
  const detail = useMessage(open ? summary.id : null);
  const from = parseMailbox(summary.from) ?? { address: summary.fromAddress };
  const isMe = mine.includes(summary.fromAddress.toLowerCase());
  const to = parseAddressList(summary.to);
  const cc = parseAddressList(summary.cc);
  const files = (detail.data?.attachments ?? []).map((a, i) => ({ a, index: i + 1 })).filter((x) => !x.a.inline);

  return (
    <motion.div layout className={cn('rounded-2xl border transition-colors', open ? 'border-line bg-raised/50' : 'border-transparent hover:bg-line/60')}>
      <div onClick={() => !fixed && setOpen(!open)} className={cn('flex w-full items-start gap-3 px-4 py-3 text-left', fixed ? 'cursor-default' : 'cursor-pointer')}>
        <Avatar name={displayName(from)} address={from.address} size={36} />
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2">
            <span className="truncate text-[13.5px] font-semibold">{isMe ? 'You' : displayName(from)}</span>
            {!isMe && <SenderMenu id={summary.id} from={from} />}
            <span className="flex-1" />
            <span className="shrink-0 text-[12px] text-faint" title={fullTime(summary.sentDate ?? summary.receivedUtc)}><Num>{fixed ? fullTime(summary.sentDate ?? summary.receivedUtc) : listTime(summary.sentDate ?? summary.receivedUtc)}</Num></span>
          </div>
          {open ? (
            <div className="truncate text-[12px] text-muted">
              to {to.map((m) => (mine.includes(m.address) ? 'me' : displayName(m))).join(', ') || '—'}
              {cc.length > 0 && `, cc ${cc.map((m) => displayName(m)).join(', ')}`}
            </div>
          ) : (
            <div className="truncate text-[12.5px] text-muted">{summary.aiGist ?? summary.snippet}</div>
          )}
        </div>
      </div>
      {open && (
        <div className="px-4 pb-4 pl-[64px]">
          {detail.isLoading ? (
            <div className="space-y-2 py-2"><div className="skeleton h-3 w-3/4" /><div className="skeleton h-3 w-2/3" /></div>
          ) : detail.data ? (
            <>
              <MessageBody detail={detail.data} />
              {files.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-2">
                  {files.map(({ a, index }) => (
                    <div key={index} className="group flex items-center gap-2.5 rounded-xl border border-line bg-raised px-3 py-2">
                      <Paperclip className="size-4 text-muted" />
                      <div className="min-w-0">
                        <div className="max-w-[220px] truncate text-[12.5px] font-medium">{a.fileName}</div>
                        <div className="text-[11px] text-faint">{formatBytes(a.sizeBytes)}</div>
                      </div>
                      <IconButton size="sm" label="Open" onClick={() => window.dts.files.openAttachment(summary.id, index, a.fileName).then((r) => (r.ok ? r.data && toast.success(`Saved ${a.fileName}`) : toast.error(r.error)))}><ExternalLink /></IconButton>
                      <IconButton size="sm" label="Save" onClick={() => window.dts.files.saveAttachment(summary.id, index, a.fileName).then((r) => (r.ok ? r.data && toast.success(`Saved ${a.fileName}`) : toast.error(r.error)))}><Download /></IconButton>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : null}
        </div>
      )}
    </motion.div>
  );
}

/** Inline "reply with AI" bar under the conversation. */
function QuickReply({ detail }: { detail: MessageDetail }) {
  const [text, setText] = useState('');
  const go = () => {
    composeReply(detail, false, text.trim() || 'Write the most helpful natural reply.');
    setText('');
  };
  return (
    <div className="ai-ring mt-6 flex items-center gap-2 rounded-2xl bg-raised/70 p-1.5 pl-4">
      <Sparkles className="size-4 shrink-0 text-accent" />
      <input
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && go()}
        placeholder="Tell the AI how to reply… e.g. “accept, propose Thursday 2pm”"
        className="h-9 flex-1 bg-transparent text-[13.5px] outline-none placeholder:text-faint"
      />
      <Button size="sm" variant="ghost" onClick={() => composeReply(detail, false)}>Write myself</Button>
      <Button size="sm" variant="ai" icon={<Send className="size-3.5" />} onClick={go}>Draft reply</Button>
    </div>
  );
}

export function Popover({ open, onClose, children, align = 'left' }: { open: boolean; onClose: () => void; children: React.ReactNode; align?: 'left' | 'right' }) {
  useEffect(() => {
    if (!open) return;
    const close = () => onClose();
    const t = setTimeout(() => window.addEventListener('click', close), 0);
    return () => {
      clearTimeout(t);
      window.removeEventListener('click', close);
    };
  }, [open, onClose]);
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0, scale: 0.96, y: -4 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.96, y: -4 }}
          transition={{ duration: 0.12 }}
          className={cn('glass absolute top-full z-40 mt-1 min-w-[220px] rounded-xl bg-raised/95 p-1 shadow-2xl', align === 'right' ? 'right-0' : 'left-0')}
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export function MenuItem({ icon, children, onClick }: { icon?: React.ReactNode; children: React.ReactNode; onClick: () => void }) {
  return (
    <button onClick={onClick} className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-left text-[13px] text-fg transition hover:bg-line [&_svg]:size-4 [&_svg]:text-muted">
      {icon}
      {children}
    </button>
  );
}
